import {
    Component, inject, ViewChild,
} from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIconRegistry, MatIcon } from '@angular/material/icon';
import { MatDrawer, MatDrawerContainer, MatDrawerContent } from '@angular/material/sidenav';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import Keycloak from 'keycloak-js';
import { filter } from 'rxjs';
import { getEnvironment } from 'src/environments/environment';
import { BreakpointService } from './services/breakpoint.service';
import { ConnectionService } from './services/connection.service';
import { OrganizationService } from './services/organization.service';
import { UserService } from './services/user.service';
import { NgIf, NgClass, NgTemplateOutlet, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { MatNavList, MatListItem, MatListItemIcon, MatListItemTitle } from '@angular/material/list';
import { ChatFeedComponent } from './components/chat-feed/chat-feed.component';
import { MatMenuTrigger, MatMenu, MatMenuContent, MatMenuItem } from '@angular/material/menu';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    imports: [
        NgIf,
        MatDrawerContainer,
        MatDrawer,
        NgClass,
        RouterLink,
        MatIconButton,
        MatIcon,
        NgTemplateOutlet,
        MatNavList,
        MatListItem,
        MatListItemIcon,
        MatListItemTitle,
        ChatFeedComponent,
        MatDrawerContent,
        NgSwitch,
        NgSwitchCase,
        NgSwitchDefault,
        RouterOutlet,
        MatButton,
        MatMenuTrigger,
        MatMenu,
        MatMenuContent,
        MatMenuItem,
    ],
})
export class AppComponent {
    /**
     * Reference to button that opens the user menu
     * This reference is used to determine the width of the menu that spawns below the button
     */
    @ViewChild('userMenuButton') userMenuButton?: MatButton;

    /**
     * Reference to angular material drawer component
     */
    @ViewChild(MatDrawer) matDrawer?: MatDrawer;

    /**
     * Names of lucide icons.
     * Icons found on https://lucide.dev/icons (MIT License)
     */
    lucideIcons = [
        'square-pen',
        'ellipsis',
        'message-circle',
        'files',
        'pencil',
        'file',
        'trash-2',
        'square-check',
        'square-x',
        'x',
        'download',
        'eye',
        'book',
        'arrow-left',
        'building-2',
        'file-search',
        'file-warning',
        'chevron-down',
        'log-out',
        'search',
        'menu',
        'users',
        'settings',
        'clipboard',
        'file-clock',
        'file-question-mark',
        'file-up',
        'external-link',
        'bot',
        'eye-off',
        'sliders-horizontal',
        'plus',
        'paperclip',
        'arrow-up',
        'file-plus-2',
    ];

    /**
     * Set to true to disable animations in the whole application. This variable set to true for a few cycles
     * when the user opens the mobile sidenav as this was the only way to prevent the animation
     */
    disableAnimations = false;

    /**
     * Name of sidenav item that should be active. This is based on the config of the route in app-routing.module
     */
    activeSidenavItem?: string;

    /**
     * Keycloak
     */
    readonly keycloak = inject(Keycloak);

    constructor(
        public user: UserService,
        public breakpoint: BreakpointService,
        public connection: ConnectionService,
        private domSanitizer: DomSanitizer,
        private matIconRegistry: MatIconRegistry,
        private router: Router,
        public organization: OrganizationService,
    ) {
        // Subscribe to NavigationEnd events
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
        ).subscribe(() => {
            // Close drawer after user navigated on small screen
            if (!this.breakpoint.lg.value && this.matDrawer?.opened) {
                // Disabale application animations for 1 tick to avoid slow closing of sidenav
                this.disableAnimations = true;
                this.matDrawer.close();
                setTimeout(() => {
                    this.disableAnimations = false;
                });
            }

            // Fetch active sidenav item from route
            let config = this.router.routerState.snapshot.root;
            while (config.firstChild) {
                config = config.firstChild;
            }
            this.activeSidenavItem = config.routeConfig?.data?.['activeSidenavItem'];
        });

        // User is not logged in
        if (!this.keycloak.authenticated) {
            // Go to custom login page if required, otherwise use default keycloak login
            if (getEnvironment().customLoginPage) {
                window.location.href = '/login.html';
            } else {
                this.keycloak.login();
            }
            return;
        }

        /**
         * Subscribe to breakpoint changes
         */
        this.breakpoint.lg.subscribe(() => {
            // Temporarily disable to avoid sidenav animation when breakpoint fires to avoid flashing sidenav
            this.disableAnimations = true;
            setTimeout(() => {
                // Re-enable anamiation next cycle
                this.disableAnimations = false;
            });
        });

        // Add custom icons
        for (const icon of this.lucideIcons) {
            this.matIconRegistry.addSvgIcon(
                icon,
                this.domSanitizer.bypassSecurityTrustResourceUrl(`../../icons/${icon}.svg`),
            );
        }
    }

    /**
     * Helper function to determine user menu width
     * @returns Width of user button, or default 220 if button width is not found
     */
    getUserMenuButtonWidth(): number {
        return this.userMenuButton?._elementRef.nativeElement.clientWidth || 220;
    }

    /**
     * Toggle drawer state
     * @param drawer Drawer reference
     */
    toggleDrawer(drawer: MatDrawer): void {
        // Temporarily disable to avoid sidenav slowly opening on mobile
        this.disableAnimations = true;
        drawer.toggle();
        setTimeout(() => {
            // Re-enable anamiation next cycle
            this.disableAnimations = false;
        });
    }

    /**
     * Call keycloak.logout with redirectUri to /login.html page if getEnvironment() has customLoginPage
     */
    logout(): void {
        if (getEnvironment().customLoginPage) {
            this.keycloak.logout({ redirectUri: `${window.location.origin}/login.html` });
        } else {
            this.keycloak.logout();
        }
    }
}
